import {
  Component,
  h,
  Host,
  Element,
  Event,
  EventEmitter,
  Listen,
  Prop,
  Watch,
} from '@stencil/core';
import { AhaEzTabList } from '../aha-ez-tab-list/aha-ez-tab-list';
import { AhaEzTabs } from '../aha-ez-tabs/aha-ez-tabs';
import { AhaEzTabPanel } from '../aha-ez-tab-panel/aha-ez-tab-panel';

export interface AhaEzTab extends HTMLAhaEzTabElement {}

@Component({
  tag: 'aha-ez-tab',
  styleUrl: 'aha-ez-tab.css',
  shadow: true,
})
export class AhaEzTab {
  @Element() host!: HTMLAhaEzTabElement;

  // Stencil supports watching on any property, but Stencil doesn't allow us to create a Prop decorator for native props. Since an element's `id` is a native prop, we can't create a `@Prop()` for it, but we can listen for changes to it.
  // eslint-disable-next-line @stencil/no-unused-watch
  @Watch('id')
  setAriaControls() {
    // When the id of the tab changes, update the `aria-controls` attribute of the tab.
    this.host.setAttribute('aria-controls', this.panelId);
  }

  @Prop({ reflect: true }) ariaSelected: string = 'false';
  @Prop({ reflect: true }) disabled?: boolean;
  @Prop({ reflect: true }) href?: string;

  @Watch('ariaSelected')
  ariaSelectedChanged() {
    const value = this.host.ariaSelected === 'true';

    // Show or hide the associated panel.
    const panel = this.panel;
    if (panel instanceof AhaEzTabPanel) {
      panel.hidden = !value;
    }

    if (value) {
      // If the tab is selected, deselect all other sibling tabs.
      const siblingTabs = this.siblingTabs;
      siblingTabs.forEach(tab => {
        if (tab instanceof AhaEzTab) {
          tab.selected = false;
        }
      });

      this.host.tabIndex = 0;
      this.selectedEvent.emit({ tab: this.host });

      // If the tab underneath has a whole page refresh, then navigate to the href.
      if (this.host.href) {
        if (window.Turbolinks) {
          window.Turbolinks.visit(this.host.href);
        } else {
          window.location.href = this.host.href;
        }
      }
    } else {
      this.host.tabIndex = -1;
      this.deselectedEvent.emit({ tab: this.host });
    }
  }

  componentDidLoad() {
    this.setAriaControls();
  }

  // When the tab is removed from the page, then hide its associated panel.
  // While the the panel is _associated_ with the tab, it cannot be guaranteed to _dependend upon_ the tab's existence on the page, and so should only be hidden when the tab is removed. This way, if, for example, this tab is replaced with another tab that is associated with the same panel, the panel will still be present.
  disconnectedCallback() {
    if (this.panel) this.panel.hidden = true;
  }

  get panelId() {
    return `${this.host.id}-panel`;
  }

  get panel() {
    if (!this.host.id) return;

    // Find the panel within the tabs container (if it exists); otherwise find it in the document.
    // This way, even if there is an id collision, the proper panel will still be found and used.
    const panelId = this.panelId;
    return (
      this.tabsContainer?.panels?.find(panel => panel.id === panelId) ||
      (document.getElementById(panelId) as HTMLAhaEzTabPanelElement)
    );
  }

  get siblingTabs() {
    const slot = this.host.assignedSlot as HTMLSlotElement;
    if (slot) {
      // Find all the tabs that are slotted into the same slot as the current tab. This is useful for when the tabs are in a tablist.
      return slot.assignedElements().filter(elem => {
        return elem !== this.host && elem.tagName === 'AHA-EZ-TAB';
      });
    } else {
      // If the tab is not in a slot, then return all other sibling tab elements in the host's immediate parent element.
      const parent = this.host.parentElement;
      if (!parent) return [];
      const children = Array.from(parent.children);
      return children.filter(elem => elem !== this.host);
    }
  }

  get selected() {
    return this.host.ariaSelected === 'true';
  }

  set selected(value: boolean) {
    this.host.ariaSelected = value.toString();
  }

  get tablist() {
    const ezTabsListRoot = this.host.assignedSlot?.getRootNode() as ShadowRoot;
    const ezTabsListElem = ezTabsListRoot?.host;
    if (ezTabsListElem && ezTabsListElem instanceof AhaEzTabList) {
      return ezTabsListElem;
    }
  }

  // Get the parent `aha-ez-tabs` element, if present.
  get tabsContainer() {
    const tabslistElem = this.tablist;
    if (!tabslistElem) return;
    const ezTabsRoot = tabslistElem.assignedSlot?.getRootNode() as ShadowRoot;
    const ezTabsElem = ezTabsRoot?.host;
    if (ezTabsElem && ezTabsElem instanceof AhaEzTabs) {
      return ezTabsElem;
    }
  }

  @Event({
    eventName: 'tabSelected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectedEvent!: EventEmitter;

  @Event({
    eventName: 'tabDeselected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  deselectedEvent!: EventEmitter;

  @Listen('click', {
    passive: false,
  })
  handleClick(event: MouseEvent) {
    if (event.defaultPrevented) return;

    // If the tab is disabled, inert, then do nothing.
    if (
      this.host.hasAttribute('disabled') ||
      this.host.ariaDisabled === 'true' ||
      this.host.hasAttribute('inert')
    ) {
      return;
    }

    this.selected = true;
  }

  render() {
    return (
      <Host tabindex="-1" role="tab">
        <slot></slot>
        <div id="border"></div>
      </Host>
    );
  }
}
